import { useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { Text, Trail } from "@react-three/drei";
import { Group, Mesh, Vector3 } from "three";
import gsap from "gsap";
import { useGameStore } from "../game/useGameStore";
import { KATAS, KataDefinition, Kamae } from "../game/katas";

const BLADE_LENGTH = 0.7;
const LOOP_PAUSE = 2.0;

// Ghost body sits at a fixed position slightly to the side of the blade demo
const BODY_X = 0.9;
const BODY_Z = -1.1;

// ─── Kamae arm positions relative to the body center ─────────────────────────
// Each entry: [leftHandOffset, rightHandOffset] from body center [BODY_X, 1.3, BODY_Z]
const KAMAE_ARMS: Record<Kamae, { lx: number; ly: number; rx: number; ry: number }> = {
  jodan:   { lx: -0.28, ly:  0.55, rx:  0.20, ry:  0.60 }, // both arms up
  chudan:  { lx: -0.22, ly:  0.10, rx:  0.16, ry:  0.10 }, // forward, mid height
  gedan:   { lx: -0.18, ly: -0.15, rx:  0.12, ry: -0.10 }, // arms low
  hasso:   { lx: -0.24, ly:  0.15, rx:  0.08, ry:  0.45 }, // right arm up/side
  waki:    { lx: -0.20, ly:  0.05, rx:  0.28, ry: -0.05 }, // right arm behind
  transit: { lx: -0.20, ly:  0.10, rx:  0.14, ry:  0.08 }, // neutral
};

// ─── Ghost Body Component ─────────────────────────────────────────────────────
interface GhostBodyProps {
  kamaeRef: React.MutableRefObject<Kamae>;
}

function GhostBody({ kamaeRef }: GhostBodyProps) {
  const headRef   = useRef<Mesh | null>(null);
  const torsoRef  = useRef<Mesh | null>(null);
  const lArmRef   = useRef<Mesh | null>(null);
  const rArmRef   = useRef<Mesh | null>(null);
  const lHandRef  = useRef<Mesh | null>(null);
  const rHandRef  = useRef<Mesh | null>(null);

  const GHOST_MAT = { color: "#88aaff", emissive: "#4466cc", emissiveIntensity: 0.8, transparent: true, opacity: 0.35 };

  useFrame(() => {
    const arms = KAMAE_ARMS[kamaeRef.current];
    if (lHandRef.current) {
      lHandRef.current.position.set(BODY_X + arms.lx, 1.3 + arms.ly, BODY_Z);
    }
    if (rHandRef.current) {
      rHandRef.current.position.set(BODY_X + arms.rx, 1.3 + arms.ry, BODY_Z);
    }
    // Arm shafts: midpoint between shoulder and hand
    if (lArmRef.current && lHandRef.current) {
      const ly = 1.3 + arms.ly;
      lArmRef.current.position.set(BODY_X + arms.lx * 0.5, (1.55 + ly) / 2, BODY_Z);
      const len = Math.hypot(arms.lx * 0.5, ly - 1.55 + 0.25);
      lArmRef.current.scale.y = Math.max(0.1, len / 0.35);
    }
    if (rArmRef.current && rHandRef.current) {
      const ry = 1.3 + arms.ry;
      rArmRef.current.position.set(BODY_X + arms.rx * 0.5, (1.55 + ry) / 2, BODY_Z);
      const len = Math.hypot(arms.rx * 0.5, ry - 1.55 + 0.25);
      rArmRef.current.scale.y = Math.max(0.1, len / 0.35);
    }
  });

  return (
    <>
      {/* Head */}
      <mesh ref={headRef} position={[BODY_X, 1.75, BODY_Z]}>
        <sphereGeometry args={[0.1, 8, 8]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Torso */}
      <mesh ref={torsoRef} position={[BODY_X, 1.35, BODY_Z]}>
        <cylinderGeometry args={[0.08, 0.1, 0.45, 8]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Hips */}
      <mesh position={[BODY_X, 1.08, BODY_Z]}>
        <sphereGeometry args={[0.09, 8, 8]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Left arm shaft */}
      <mesh ref={lArmRef} position={[BODY_X - 0.14, 1.5, BODY_Z]}>
        <cylinderGeometry args={[0.025, 0.025, 0.35, 6]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Right arm shaft */}
      <mesh ref={rArmRef} position={[BODY_X + 0.10, 1.5, BODY_Z]}>
        <cylinderGeometry args={[0.025, 0.025, 0.35, 6]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Left hand */}
      <mesh ref={lHandRef} position={[BODY_X - 0.28, 1.4, BODY_Z]}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Right hand */}
      <mesh ref={rHandRef} position={[BODY_X + 0.20, 1.4, BODY_Z]}>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>

      {/* Legs */}
      <mesh position={[BODY_X - 0.07, 0.72, BODY_Z]}>
        <cylinderGeometry args={[0.04, 0.04, 0.55, 6]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>
      <mesh position={[BODY_X + 0.07, 0.72, BODY_Z]}>
        <cylinderGeometry args={[0.04, 0.04, 0.55, 6]} />
        <meshStandardMaterial {...GHOST_MAT} />
      </mesh>
    </>
  );
}

// ─── Ghost Blade Component ────────────────────────────────────────────────────
interface GhostBladeProps {
  kata: KataDefinition;
  kamaeRef: React.MutableRefObject<Kamae>;
  labelRef: React.MutableRefObject<string>;
  postureRef: React.MutableRefObject<string>;
  versionRef: React.MutableRefObject<number>;
}

function GhostBlade({ kata, kamaeRef, labelRef, postureRef, versionRef }: GhostBladeProps) {
  const groupRef  = useRef<Group | null>(null);
  const tipProxy  = useRef({ x: kata.frames[0].tip[0],  y: kata.frames[0].tip[1],  z: kata.frames[0].tip[2]  });
  const gripProxy = useRef({ x: kata.frames[0].grip[0], y: kata.frames[0].grip[1], z: kata.frames[0].grip[2] });

  useEffect(() => {
    const tip  = tipProxy.current;
    const grip = gripProxy.current;

    function buildTimeline() {
      const tl = gsap.timeline({ onComplete: () => { gsap.delayedCall(LOOP_PAUSE, buildTimeline); } });

      for (let i = 1; i < kata.frames.length; i++) {
        const f  = kata.frames[i];
        const fp = kata.frames[i - 1];
        const dt = f.t - fp.t;

        tl.to(tip,  { x: f.tip[0],  y: f.tip[1],  z: f.tip[2],  duration: dt, ease: "power2.inOut" }, fp.t);
        tl.to(grip, { x: f.grip[0], y: f.grip[1], z: f.grip[2], duration: dt, ease: "power2.inOut" }, fp.t);

        if (f.label || f.kamae || f.postureDesc) {
          tl.call(() => {
            if (f.label)       labelRef.current   = f.label;
            if (f.kamae)       kamaeRef.current   = f.kamae;
            if (f.postureDesc) postureRef.current = f.postureDesc;
            versionRef.current++;
          }, [], fp.t);
        }
      }

      const last = kata.frames[kata.frames.length - 1];
      tl.call(() => { labelRef.current = ""; }, [], last.t + 0.8);

      return tl;
    }

    const tl = buildTimeline();
    return () => { tl.kill(); gsap.killTweensOf(tip); gsap.killTweensOf(grip); };
  }, [kata]);

  const tipVec  = useRef(new Vector3());
  const gripVec = useRef(new Vector3());
  const mid     = useRef(new Vector3());

  useFrame(() => {
    if (!groupRef.current) return;
    const t = tipProxy.current;
    const g = gripProxy.current;
    tipVec.current.set(t.x, t.y, t.z);
    gripVec.current.set(g.x, g.y, g.z);
    mid.current.lerpVectors(tipVec.current, gripVec.current, 0.5);
    groupRef.current.position.copy(mid.current);
    groupRef.current.lookAt(tipVec.current);
  });

  return (
    <group ref={groupRef}>
      <Trail width={0.04} length={12} color="#88ccff" attenuation={(w) => w * w}>
        <mesh position={[0, 0, BLADE_LENGTH / 2]}>
          <boxGeometry args={[0.02, 0.02, BLADE_LENGTH]} />
          <meshStandardMaterial
            color="#aaddff"
            emissive="#88ccff"
            emissiveIntensity={1.2}
            transparent
            opacity={0.55}
          />
        </mesh>
      </Trail>
    </group>
  );
}

// ─── Posture Panel (Text 3D) ──────────────────────────────────────────────────
function PosturePanel({
  labelRef,
  postureRef,
  versionRef,
}: {
  labelRef: React.MutableRefObject<string>;
  postureRef: React.MutableRefObject<string>;
  versionRef: React.MutableRefObject<number>;
}) {
  const labelTextRef   = useRef<any>(null);
  const postureTextRef = useRef<any>(null);
  const lastVersion    = useRef(-1);

  useFrame(() => {
    if (versionRef.current === lastVersion.current) return;
    lastVersion.current = versionRef.current;
    if (labelTextRef.current)   labelTextRef.current.text   = labelRef.current;
    if (postureTextRef.current) postureTextRef.current.text = postureRef.current;
  });

  return (
    <>
      {/* Waza label */}
      <Text
        ref={labelTextRef}
        position={[-0.6, 1.85, -1.0]}
        fontSize={0.09}
        color="#ffdd88"
        anchorX="center"
        anchorY="top"
        maxWidth={0.9}
        textAlign="center"
      >
        {""}
      </Text>

      {/* Posture description */}
      <Text
        ref={postureTextRef}
        position={[-0.6, 1.65, -1.0]}
        fontSize={0.065}
        color="#ccbbaa"
        anchorX="center"
        anchorY="top"
        maxWidth={1.0}
        textAlign="left"
        lineHeight={1.5}
      >
        {""}
      </Text>
    </>
  );
}

// ─── KataDemo (exported) ──────────────────────────────────────────────────────
export function KataDemo() {
  const activeKata = useGameStore((s) => s.activeKata);
  const kata = KATAS.find((k) => k.id === activeKata) ?? KATAS[0];

  // Shared mutable refs updated by GhostBlade, read by PosturePanel + GhostBody
  const kamaeRef   = useRef<Kamae>(kata.frames[0].kamae ?? "chudan");
  const labelRef   = useRef(kata.frames[0].label ?? "");
  const postureRef = useRef(kata.frames[0].postureDesc ?? "");
  const versionRef = useRef(0);

  return (
    <>
      <GhostBlade
        key={kata.id}
        kata={kata}
        kamaeRef={kamaeRef}
        labelRef={labelRef}
        postureRef={postureRef}
        versionRef={versionRef}
      />

      <GhostBody kamaeRef={kamaeRef} />

      <PosturePanel
        labelRef={labelRef}
        postureRef={postureRef}
        versionRef={versionRef}
      />

      {/* Kata title */}
      <Text position={[0, 2.38, -1.5]} fontSize={0.15} color="#ffdd88" anchorX="center" anchorY="middle">
        {`${kata.nameJP}  ${kata.name}`}
      </Text>
      <Text position={[0, 2.22, -1.5]} fontSize={0.08} color="#cc9955" anchorX="center" anchorY="middle">
        {kata.waza}
      </Text>
      <Text position={[0, 2.10, -1.5]} fontSize={0.07} color="#886644" anchorX="center" anchorY="middle">
        {kata.description}
      </Text>
    </>
  );
}
